import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { loadPublicResourcesForHub } from "@/lib/query-registry";
import { ResourceHub, collectionPageJsonLd } from "@/components/site/ResourceHub";
import { SITE_URL } from "@/lib/i18n";
import coverMig from "@/assets/cover-pillar-migration.jpg";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/resources/migration";
const ALT = "/es/recursos/migracion";
const TITLE = "Shopify Migration Resources: By Source Platform · Shugert";
const DESC =
  "Shopify migration guides for WooCommerce, Magento, BigCommerce, custom platforms, legacy Shopify builds and Shopify Plus decisions, including data, SEO, integrations and launch risk.";

export const Route = createFileRoute("/resources/migration")({
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    return loadPublicResourcesForHub(context.queryClient);
  },
  head: ({ loaderData }) => {
    const resources = (loaderData as any)?.resources ?? [];
    const urls = resources
      .filter((r: any) =>
        (r.tags_en ?? []).join(" ").toLowerCase().match(/migration|replatform|bigcommerce|magento|woocommerce/),
      )
      .map((r: any) => `${SITE_URL}/resources/${r.slug}`);
    return buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: coverMig,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Migration", path: PATH },
        ]),
        collectionPageJsonLd("Shopify Migration Resources", `${SITE_URL}${PATH}`, DESC, urls),
      ],
    });
  },
  component: () => (
    <SiteShell>
      <ResourceHub
        locale="en"
        cluster="migration"
        copy={{
          eyebrow: "Migration resources",
          title: "Shopify migration resources: organized by source platform and operating requirement.",
          intro:
            "Use this hub for migrations from WooCommerce, Magento, BigCommerce, custom platforms and legacy Shopify builds. Most projects can target standard Shopify; the Shopify Plus guides cover the premium path only when B2B, checkout extensibility, Functions, complex Markets, multiple stores or enterprise integrations justify it.",
          serviceLabel: "Explore Shopify migration services",
          serviceHref: "/shopify-migration",
          articlesTitle: "Migration guides",
          articlesEmpty: "More migration guides coming soon. Need help now? Request a scope.",
          read: "Read",
          min: "min",
          by: "By",
          updated: "Updated",
          backLabel: "Resources",
          backHref: "/resources",
        }}
      />
    </SiteShell>
  ),
});
